<template>
  <div class="discrepancy-images">
    <div class="images-wrapper">
      <!-- Existing images -->
      <div
        v-for="(image, imageIndex) of records"
        :key="image.id"
        class="image-tile"
      >
        <b-img
          :src="imageUrl(image)"
          class="image-thumb"
          @click="openPreview(imageIndex)"
        />

        <div class="image-overlay">
          <b-button
            variant="flat-light"
            size="sm"
            class="btn-icon overlay-btn"
            title="View"
            @click="openPreview(imageIndex)"
          >
            <feather-icon
              icon="EyeIcon"
              size="18"
            />
          </b-button>

          <b-button
            variant="flat-danger"
            size="sm"
            class="btn-icon overlay-btn"
            title="Delete"
            :disabled="uploading || deletingImageId === image.id"
            @click="confirmDelete(image)"
          >
            <b-spinner
              v-if="deletingImageId === image.id"
              small
            />
            <feather-icon
              v-else
              icon="Trash2Icon"
              size="18"
            />
          </b-button>
        </div>
      </div>

      <!-- Upload tile -->
      <div
        v-if="canUpload"
        class="image-tile upload-tile"
        :class="{ disabled: uploading }"
        @click="triggerFilePicker"
      >
        <b-spinner
          v-if="uploading"
          variant="primary"
        />
        <template v-else>
          <feather-icon
            icon="UploadCloudIcon"
            size="42"
            class="text-primary"
          />
          <span class="upload-text">Upload Image</span>
          <small class="text-muted">{{ records.length }} / {{ maxImages }}</small>
        </template>
      </div>

      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        multiple
        class="d-none"
        @change="handleFileChange"
      >
    </div>

    <!-- Delete confirmation -->
    <b-modal
      v-model="displayDeleteConfirmation"
      title="Delete Image"
      centered
      @hidden="imageToDelete = null"
    >
      <b-card-text>
        <p>Are you sure you want to delete this image?</p>
        <p>Once deleted, it can not be recovered!</p>
      </b-card-text>

      <template #modal-footer="{ cancel }">
        <b-button
          variant="secondary"
          @click="cancel()"
        >
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="!!deletingImageId"
          @click="deleteImage"
        >
          Delete Image
          <b-spinner
            v-if="deletingImageId"
            small
          />
        </b-button>
      </template>
    </b-modal>

    <!-- Preview -->
    <b-modal
      v-model="displayPreview"
      size="lg"
      centered
      hide-footer
      title="Discrepancy Image"
    >
      <div class="text-center">
        <b-img
          v-if="previewImage"
          :src="imageUrl(previewImage)"
          fluid
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BImg, BButton, BSpinner, BModal, BCardText,
} from 'bootstrap-vue'
import { isEqual, cloneDeep } from 'lodash'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BImg, BButton, BSpinner, BModal, BCardText,
  },
  props: {
    discrepancyImages: {
      type: Array,
      required: true,
    },
    discrepancyId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      records: [],
      maxImages: 5,
      uploading: false,
      deletingImageId: null,
      imageToDelete: null,
      displayDeleteConfirmation: false,
      displayPreview: false,
      previewImage: null,
    }
  },
  computed: {
    canUpload() {
      return this.records.length < this.maxImages
    },
    remainingSlots() {
      return this.maxImages - this.records.length
    },
  },
  watch: {
    discrepancyImages: {
      handler(val) {
        if (!isEqual(val, this.records)) {
          this.setInternalState()
        }
      },
      deep: true,
    },
  },
  created() {
    this.setInternalState()
  },
  methods: {
    setInternalState() {
      this.records = cloneDeep(this.discrepancyImages) || []
    },
    imageUrl(image) {
      if (!image) return ''
      return image.image || image.file || image.url || ''
    },
    openPreview(index) {
      this.previewImage = this.records[index]
      this.displayPreview = true
    },
    triggerFilePicker() {
      if (this.uploading) return
      this.$refs.fileInput.click()
    },
    showToast(title, isError = false) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: isError ? 'AlertTriangleIcon' : 'CheckIcon',
          variant: isError ? 'danger' : 'success',
        },
      })
    },
    resetFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    handleFileChange(event) {
      const files = Array.from(event.target.files || [])
      // reset so selecting the same file again still fires change
      this.resetFileInput()

      if (!files.length) return

      if (files.length > this.remainingSlots) {
        this.showToast(`You can upload only ${this.remainingSlots} more image(s)`, true)
        return
      }

      const invalidFile = files.find(file => !file.type.startsWith('image/'))
      if (invalidFile) {
        this.showToast('Only image files are allowed', true)
        return
      }

      this.uploadImages(files)
    },
    async uploadImages(files) {
      try {
        this.uploading = true

        const formData = new FormData()
        files.forEach(file => {
          formData.append('file', file)
        })

        await axios.post(
          `/tickets/discrepancy_images/${this.discrepancyId}/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )

        this.uploading = false
        this.showToast(files.length > 1 ? 'Images uploaded successfully' : 'Image uploaded successfully')
        this.$emit('onDiscrepancyImagesUpdate')
      } catch (error) {
        this.uploading = false
        this.showToast(error?.response?.data?.detail || 'Error uploading image', true)
      }
    },
    confirmDelete(image) {
      this.imageToDelete = image
      this.displayDeleteConfirmation = true
    },
    async deleteImage() {
      if (!this.imageToDelete) return

      try {
        this.deletingImageId = this.imageToDelete.id

        await axios.delete(`/tickets/discrepancy_image/${this.imageToDelete.id}/`)

        this.deletingImageId = null
        this.displayDeleteConfirmation = false
        this.imageToDelete = null

        this.showToast('Image deleted successfully')
        this.$emit('onDiscrepancyImagesUpdate')
      } catch (error) {
        this.deletingImageId = null
        this.showToast(error?.response?.data?.detail || 'Error deleting image', true)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.images-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.image-tile {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #ebe9f1;
}

.image-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.image-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

.image-tile:hover .image-overlay {
  opacity: 1;
}

.overlay-btn {
  background: rgba(255, 255, 255, 0.9) !important;
}

.upload-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  border: 2px dashed #d8d6de;
  background: #fafafa;
  transition: border-color 0.2s ease-in-out, background 0.2s ease-in-out;

  &:hover {
    border-color: var(--primary, #7367f0);
    background: #f5f4ff;
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.upload-text {
  font-size: 0.9rem;
  font-weight: 500;
}
</style>
